/**
 * localStorage helpers with typed keys and JSON serialization.
 */

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'bagrut_token',
  STUDENT: 'bagrut_student',
  TRACK: 'bagrut_track',
  LANG: 'bagrut_lang',
  REMEMBER: 'bagrut_remember',
  SOUND: 'bagrut_sound',
  DAILY_GOAL: 'bagrut_daily_goal',
  Q_STATS: 'bagrut_q_stats',
  WEEKLY: 'bagrut_weekly',
  EXAM_HISTORY: 'bagrut_exam_history',
  FLAGGED: 'bagrut_flagged',
  DAILY: 'bagrut_daily',
  LIFETIME_Q: 'bagrut_lifetime_q',
  LAST_UNIT: 'bagrut_last_unit',
  STUDY_TIME: 'bagrut_study_time',
  TOPIC_STATS: 'bagrut_topic_stats',
} as const;

export function getJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function setJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('[Storage] Failed to write:', key, e);
  }
}

export function removeItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('[Storage] Failed to remove:', key, e);
  }
}

// Plain-string get/set with try/catch. localStorage throws in Safari
// private mode and when quota is exceeded — bare calls would crash
// the auth bootstrap.
export function getString(key: string, fallback = ''): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

export function setString(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn('[Storage] Failed to write:', key, e);
  }
}

// ── Question Stats ───────────────────────────────────────────────
export interface QStat {
  correct: number;
  wrong: number;
  last: string;
  hint: number;
}

export function getQStats(): Record<string, QStat> {
  return getJSON(STORAGE_KEYS.Q_STATS, {});
}

export function updateQStat(
  questionId: string,
  correct: boolean,
  hintUsed: boolean
): void {
  const stats = getQStats();
  const prev = stats[questionId] || { correct: 0, wrong: 0, last: '', hint: 0 };
  stats[questionId] = {
    correct: prev.correct + (correct ? 1 : 0),
    wrong: prev.wrong + (correct ? 0 : 1),
    last: new Date().toISOString().slice(0, 10),
    hint: prev.hint + (hintUsed ? 1 : 0),
  };
  setJSON(STORAGE_KEYS.Q_STATS, stats);
}

// ── Exam History ─────────────────────────────────────────────────
export function getExamHistory(): Array<{
  date: string;
  pct: number;
  correct: number;
  total: number;
  track: string;
}> {
  return getJSON(STORAGE_KEYS.EXAM_HISTORY, []);
}

export function addExamResult(result: {
  pct: number;
  correct: number;
  total: number;
  track: string;
}): void {
  const history = getExamHistory();
  history.push({
    ...result,
    date: new Date().toISOString().slice(0, 10),
  });
  // Keep last 50
  if (history.length > 50) history.splice(0, history.length - 50);
  setJSON(STORAGE_KEYS.EXAM_HISTORY, history);
}

// ── Flagged Questions ────────────────────────────────────────────
export function getFlaggedQuestions(): string[] {
  return getJSON(STORAGE_KEYS.FLAGGED, []);
}

export function toggleFlagged(questionId: string): boolean {
  const flagged = getFlaggedQuestions();
  const idx = flagged.indexOf(questionId);
  if (idx >= 0) {
    flagged.splice(idx, 1);
    setJSON(STORAGE_KEYS.FLAGGED, flagged);
    return false;
  } else {
    flagged.push(questionId);
    setJSON(STORAGE_KEYS.FLAGGED, flagged);
    return true;
  }
}

// ── Daily Counter ────────────────────────────────────────────────
export function incrementDailyCount(): number {
  const today = new Date().toISOString().slice(0, 10);
  const data = getJSON<{ date: string; count: number }>(STORAGE_KEYS.DAILY, {
    date: today,
    count: 0,
  });
  if (data.date !== today) {
    data.date = today;
    data.count = 0;
  }
  data.count++;
  setJSON(STORAGE_KEYS.DAILY, data);
  return data.count;
}

export function getDailyCount(): number {
  const today = new Date().toISOString().slice(0, 10);
  const data = getJSON<{ date: string; count: number }>(STORAGE_KEYS.DAILY, {
    date: today,
    count: 0,
  });
  return data.date === today ? data.count : 0;
}

// ── Lifetime Counter ─────────────────────────────────────────────
export function incrementLifetimeCount(): number {
  const count = getJSON<number>(STORAGE_KEYS.LIFETIME_Q, 0) + 1;
  setJSON(STORAGE_KEYS.LIFETIME_Q, count);
  return count;
}

export function getLifetimeCount(): number {
  return getJSON<number>(STORAGE_KEYS.LIFETIME_Q, 0);
}

// ── Weekly Tracking ─────────────────────────────────────────────
/** Update weekly activity data — called after each answer. */
export function updateWeeklyData(correct: boolean): void {
  const today = new Date().toISOString().slice(0, 10);
  const data = getJSON<{ days: { date: string; count: number; correct: number }[] }>(
    STORAGE_KEYS.WEEKLY,
    { days: [] }
  );
  const last = data.days[data.days.length - 1];
  if (last && last.date === today) {
    last.count++;
    if (correct) last.correct++;
  } else {
    data.days.push({ date: today, count: 1, correct: correct ? 1 : 0 });
  }
  // Keep last 30 days
  if (data.days.length > 30) data.days.splice(0, data.days.length - 30);
  setJSON(STORAGE_KEYS.WEEKLY, data);
}

// ── Topic Stats ─────────────────────────────────────────────────
/** Update per-topic accuracy stats — called after each answer. */
export function updateTopicStat(topic: string, correct: boolean): void {
  if (!topic) return;
  const data = getJSON<Record<string, { correct: number; total: number }>>('bagrut_topic_stats', {});
  const prev = data[topic] || { correct: 0, total: 0 };
  data[topic] = {
    correct: prev.correct + (correct ? 1 : 0),
    total: prev.total + 1,
  };
  setJSON('bagrut_topic_stats', data);
}

export interface TopicMastery { topic: string; correct: number; total: number; pct: number }

/**
 * Pedagogical 4-level mastery rubric (Israeli elementary doc, but applies
 * to every grade). A topic moves up the rubric as the student demonstrates
 * deeper understanding:
 *
 *   0 — לא התחיל     — no attempt yet
 *   1 — מזהה          — can identify the topic (≥1 correct)
 *   2 — מבצע          — procedural fluency (≥3 correct, ≥60%)
 *   3 — מסביר         — conceptual understanding (≥5 correct, ≥75%, low hint use)
 *   4 — מעביר         — transfers to new contexts (10+ correct, ≥85% — used as proxy)
 *
 * We derive the level from existing bagrut_topic_stats so no new storage is
 * needed. Hint use is approximated by per-question hint counts from qStats.
 */
export type RubricLevel = 0 | 1 | 2 | 3 | 4;

export const RUBRIC_LEVEL_NAMES: Record<RubricLevel, string> = {
  0: 'לא התחיל',
  1: 'מזהה',
  2: 'מבצע',
  3: 'מסביר',
  4: 'מעביר',
};

export const RUBRIC_LEVEL_DESC: Record<RubricLevel, string> = {
  0: 'עוד לא תרגלת את הנושא',
  1: 'יודע לזהות את הנושא בשאלה',
  2: 'יודע לבצע את הפעולה נכון',
  3: 'מבין למה החישוב עובד',
  4: 'משתמש ברעיון במצב חדש',
};

export function getTopicRubricLevel(topic: string): RubricLevel {
  const data = getJSON<Record<string, { correct: number; total: number }>>('bagrut_topic_stats', {});
  const s = data[topic];
  if (!s || s.total === 0 || s.correct === 0) return 0;
  const pct = (s.correct / s.total) * 100;
  if (s.correct >= 10 && pct >= 85) return 4;
  if (s.correct >= 5 && pct >= 75) return 3;
  if (s.correct >= 3 && pct >= 60) return 2;
  return 1;
}

// ── Mastered topics ─────────────────────────────────────────────
/**
 * A topic is "mastered" once it reaches rubric L4 (transfer level).
 * Mastery is permanent — even if accuracy later dips, the achievement
 * stays earned. This is intentional: removing badges discourages
 * exploration and feels punishing.
 */
export interface MasteredTopic {
  topic: string;
  achieved_at: number; // ms epoch
}

export function getMasteredTopics(): MasteredTopic[] {
  return getJSON<MasteredTopic[]>('bagrut_mastered_topics', []);
}

/** Returns true if newly added; false if topic was already mastered. */
export function markTopicMastered(topic: string): boolean {
  if (!topic) return false;
  const list = getMasteredTopics();
  if (list.some(m => m.topic === topic)) return false;
  list.push({ topic, achieved_at: Date.now() });
  setJSON('bagrut_mastered_topics', list);
  return true;
}

/**
 * Check the topic's current rubric level and persist mastery if it just
 * reached L4. Returns the topic name when a *new* mastery was recorded,
 * or null if the topic isn't at L4 yet or was already mastered. The
 * caller uses the return value to trigger the celebration.
 */
export function checkAndMarkMastery(topic: string): string | null {
  if (!topic) return null;
  if (getTopicRubricLevel(topic) !== 4) return null;
  return markTopicMastered(topic) ? topic : null;
}

/**
 * Returns the masteries achieved during the current calendar week
 * (Sunday → Saturday in local time, matching Israeli convention).
 * Used to surface a "🔥 שבוע פורה" bonus when a kid masters multiple
 * topics in one week.
 */
export function getMasteriesThisWeek(): MasteredTopic[] {
  const now = new Date();
  const dow = now.getDay(); // 0 = Sun
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - dow);
  const startMs = start.getTime();
  return getMasteredTopics().filter(m => m.achieved_at >= startMs);
}

/**
 * Returns the masteries achieved since midnight (local time).
 * Drives daily mastery quests.
 */
export function getMasteriesToday(): MasteredTopic[] {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const startMs = start.getTime();
  return getMasteredTopics().filter(m => m.achieved_at >= startMs);
}

/**
 * Aggregate topic stats sorted for stats screens. `minTotal` filters out
 * topics seen only once or twice — those are too noisy to surface as
 * "your weakest" / "your strongest".
 */
export function getTopicMasteryList(opts: { minTotal?: number } = {}): TopicMastery[] {
  const minTotal = opts.minTotal ?? 3;
  const data = getJSON<Record<string, { correct: number; total: number }>>('bagrut_topic_stats', {});
  const list: TopicMastery[] = [];
  for (const topic in data) {
    const s = data[topic];
    if (!s || s.total < minTotal) continue;
    list.push({
      topic,
      correct: s.correct,
      total: s.total,
      pct: Math.round((s.correct / s.total) * 100),
    });
  }
  return list;
}

// ── Study Time ──────────────────────────────────────────────────
/** Add elapsed milliseconds to today's study time. */
export function addStudyTime(ms: number): void {
  if (ms <= 0) return;
  const today = new Date().toISOString().slice(0, 10);
  const data = getJSON<{ date: string; ms: number }>('bagrut_study_time_today', { date: today, ms: 0 });
  if (data.date !== today) {
    data.date = today;
    data.ms = 0;
  }
  data.ms += ms;
  setJSON('bagrut_study_time_today', data);
}

/** Get today's study time in ms. */
export function getStudyTimeToday(): number {
  const today = new Date().toISOString().slice(0, 10);
  const data = getJSON<{ date: string; ms: number }>('bagrut_study_time_today', { date: today, ms: 0 });
  return data.date === today ? data.ms : 0;
}

// ── Question Timing (for estimated practice time) ───────────────
/**
 * Maintain a rolling exponential-weighted average of question answer time
 * so the unit picker can show "~X minutes" estimates that improve as the
 * student answers more questions. New samples weighted at α=0.1.
 */
const Q_TIME_AVG_KEY = 'bagrut_q_time_avg';
const DEFAULT_AVG_MS = 50_000; // 50s — reasonable default for Bagrut math MC

export function recordQuestionTime(ms: number): void {
  if (ms <= 1000 || ms > 600_000) return; // ignore <1s or >10min outliers
  const data = getJSON<{ avg: number; n: number }>(Q_TIME_AVG_KEY, { avg: DEFAULT_AVG_MS, n: 0 });
  const alpha = data.n < 5 ? 0.4 : 0.1; // bootstrap faster early on
  const newAvg = data.n === 0 ? ms : data.avg * (1 - alpha) + ms * alpha;
  setJSON(Q_TIME_AVG_KEY, { avg: Math.round(newAvg), n: data.n + 1 });
}

export function getAvgQuestionTimeMs(): number {
  const data = getJSON<{ avg: number; n: number }>(Q_TIME_AVG_KEY, { avg: DEFAULT_AVG_MS, n: 0 });
  return data.avg;
}

// ── Last Practiced Unit (for Continue chip) ─────────────────────
export interface LastSession {
  unit_id: string;
  unit_title: string;
  correct: number;
  total: number;
  pct: number;
  ts: number; // epoch ms
}

const LAST_SESSION_KEY = 'bagrut_last_session';
const SESSION_FRESH_DAYS = 7;

export function setLastSession(s: LastSession): void {
  setJSON(LAST_SESSION_KEY, s);
}

/** Returns null if no session, session has unit_id "all", or it's older than 7 days. */
export function getLastSession(): LastSession | null {
  const s = getJSON<LastSession | null>(LAST_SESSION_KEY, null);
  if (!s || !s.unit_id || s.unit_id === 'all') return null;
  const age = Date.now() - s.ts;
  if (age > SESSION_FRESH_DAYS * 24 * 60 * 60 * 1000) return null;
  return s;
}

export function clearLastSession(): void {
  removeItem(LAST_SESSION_KEY);
}

// ── Mastery Snapshots (for "↗️ +X%" trend on UnitSelect) ─────────
const MASTERY_SNAPSHOTS_KEY = 'bagrut_mastery_snapshots';

export interface MasterySnapshot {
  date: string;            // YYYY-MM-DD (one snapshot per day)
  pct: number;             // mastered / attempted * 100, rounded
  total_attempted: number; // unique question IDs answered ever
  total_mastered: number;  // attempted with ≥70% accuracy
}

/**
 * Record overall mastery for today. Replaces today's existing entry if present.
 * Keeps last 90 days. No-op if user has no Q stats yet (cold start).
 */
export function recordMasterySnapshot(): void {
  const stats = getQStats();
  const ids = Object.keys(stats);
  if (ids.length === 0) return;
  let mastered = 0;
  for (const id of ids) {
    const s = stats[id];
    const total = s.correct + s.wrong;
    if (total > 0 && s.correct > 0 && s.correct / total >= 0.7) mastered++;
  }
  const today = new Date().toISOString().slice(0, 10);
  const snap: MasterySnapshot = {
    date: today,
    pct: Math.round((mastered / ids.length) * 100),
    total_attempted: ids.length,
    total_mastered: mastered,
  };
  const list = getJSON<MasterySnapshot[]>(MASTERY_SNAPSHOTS_KEY, []);
  const idx = list.findIndex(s => s.date === today);
  if (idx >= 0) list[idx] = snap; else list.push(snap);
  if (list.length > 90) list.splice(0, list.length - 90);
  setJSON(MASTERY_SNAPSHOTS_KEY, list);
}

/**
 * Returns current mastery + value from ~7 days ago (closest snapshot ≤ that
 * date). Null if no snapshots, or only one without ≥1 day of history.
 */
export function getMasteryTrend(): { current: number; weekAgo: number; delta: number } | null {
  const list = getJSON<MasterySnapshot[]>(MASTERY_SNAPSHOTS_KEY, []);
  if (list.length === 0) return null;
  const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
  const current = sorted[sorted.length - 1].pct;
  const weekAgoDate = new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10);
  // Pick the most recent snapshot whose date ≤ weekAgoDate
  let weekAgo: MasterySnapshot | undefined;
  for (const s of sorted) {
    if (s.date <= weekAgoDate) weekAgo = s;
    else break;
  }
  if (!weekAgo) return null;
  return { current, weekAgo: weekAgo.pct, delta: current - weekAgo.pct };
}

// ── Mistake Pattern Tracking ────────────────────────────────────
/**
 * Track every wrong-answer mistake_label so the summary screen can surface
 * recurring patterns ("you fell for 'היפוך משלים' 4 times in 2 weeks").
 * Uses an event log keyed by epoch ms; old entries are trimmed to keep the
 * payload small.
 */
export interface MistakeEntry {
  ts: number;
  qid: string;
  label: string;
  quality: 'good' | 'ambiguous' | 'weak_generated';
}

const MISTAKES_KEY = 'bagrut_mistakes';
const MISTAKES_KEEP = 500;

export function recordMistake(entry: Omit<MistakeEntry, 'ts'>): void {
  if (!entry.label) return;
  const list = getJSON<MistakeEntry[]>(MISTAKES_KEY, []);
  list.push({ ...entry, ts: Date.now() });
  if (list.length > MISTAKES_KEEP) list.splice(0, list.length - MISTAKES_KEEP);
  setJSON(MISTAKES_KEY, list);
}

/**
 * Return the user's recent mistakes, newest first, deduplicated by question id
 * (most recent occurrence wins). Used by the Mistakes Review screen to drive
 * a batch lookup for question content.
 */
export function getRecentMistakes(opts: { windowDays?: number; limit?: number; excludeWeak?: boolean } = {}): MistakeEntry[] {
  const windowDays = opts.windowDays ?? 30;
  const limit = opts.limit ?? 50;
  const excludeWeak = opts.excludeWeak ?? true;
  const list = getJSON<MistakeEntry[]>(MISTAKES_KEY, []);
  const cutoff = Date.now() - windowDays * 86_400_000;
  const seen = new Set<string>();
  const out: MistakeEntry[] = [];
  // Walk newest-first so the first hit per qid is the most recent occurrence
  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i];
    if (e.ts < cutoff) continue;
    if (excludeWeak && e.quality === 'weak_generated') continue;
    if (seen.has(e.qid)) continue;
    seen.add(e.qid);
    out.push(e);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Spaced-repetition due queue (simplified Leitner).
 *
 * Idea: every qid where the most recent event is a mistake is on the
 * "needs review" track. Each correct answer pushes its review interval
 * out: 1d → 3d → 7d → 14d → 30d. Cap at 30d. A new mistake resets to 1d.
 *
 * We derive review state on the fly from existing data (bagrut_mistakes +
 * bagrut_q_stats) rather than maintaining a separate SR table, so it
 * works retroactively for users who already have history.
 *
 * Returns qids in priority order — most-stale (longest overdue) first.
 */
const SR_INTERVALS_DAYS = [1, 3, 7, 14, 30];

export interface DueItem {
  qid: string;
  label: string;
  /** Days overdue (negative = not yet due). */
  daysOverdue: number;
  /** ms-since-epoch of the last mistake. */
  lastMistakeTs: number;
}

export function getDueForReview(opts: { limit?: number } = {}): DueItem[] {
  const limit = opts.limit ?? 30;
  const mistakes = getJSON<MistakeEntry[]>(MISTAKES_KEY, []);
  if (mistakes.length === 0) return [];
  const qStats = getQStats();

  // Walk newest-first so the first encounter for each qid is the latest mistake.
  const seen = new Set<string>();
  const latestPerQid: MistakeEntry[] = [];
  for (let i = mistakes.length - 1; i >= 0; i--) {
    const m = mistakes[i];
    if (seen.has(m.qid)) continue;
    seen.add(m.qid);
    latestPerQid.push(m);
  }

  const now = Date.now();
  const due: DueItem[] = [];
  for (const m of latestPerQid) {
    if (m.quality === 'weak_generated') continue;
    const s = qStats[m.qid];
    // Each `correct` after the mistake bumps the interval; cap at the last slot.
    const correctsSince = s ? Math.max(0, s.correct - 1) : 0;
    const intervalDays = SR_INTERVALS_DAYS[Math.min(correctsSince, SR_INTERVALS_DAYS.length - 1)];
    const overdueMs = now - (m.ts + intervalDays * 86_400_000);
    if (overdueMs >= 0) {
      due.push({
        qid: m.qid,
        label: m.label,
        daysOverdue: Math.floor(overdueMs / 86_400_000),
        lastMistakeTs: m.ts,
      });
    }
  }

  // Most overdue first.
  due.sort((a, b) => b.daysOverdue - a.daysOverdue);
  return due.slice(0, limit);
}

/** Shortcut for badges/dashboards. */
export function getDueForReviewCount(): number {
  return getDueForReview({ limit: 999 }).length;
}

/**
 * Count distinct qids with a mistake in the recent window (default 24h).
 * Used to surface a fresh "mistakes from your last session" CTA — these
 * are NOT yet picked up by the spaced-repetition queue (whose minimum
 * interval is 1 day). Excludes weak_generated quality.
 */
export function getRecentMistakeCount(hours: number = 24): number {
  const cutoff = Date.now() - hours * 3_600_000;
  const list = getJSON<MistakeEntry[]>(MISTAKES_KEY, []);
  const seen = new Set<string>();
  let count = 0;
  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i];
    if (e.ts < cutoff) break;
    if (e.quality === 'weak_generated') continue;
    if (seen.has(e.qid)) continue;
    seen.add(e.qid);
    count++;
  }
  return count;
}

export interface MistakePattern {
  label: string;
  count: number;
  lastQuestionId: string;
  lastTs: number;
  qualities: { good: number; ambiguous: number; weak_generated: number };
}

/**
 * Aggregate mistake events into recurring patterns. Defaults: 14-day window,
 * minimum 2 occurrences to surface, weak_generated labels excluded (they're
 * not diagnostic enough to act on).
 */
export function getMistakePatterns(opts?: {
  windowDays?: number;
  minCount?: number;
  excludeWeak?: boolean;
}): MistakePattern[] {
  const windowDays = opts?.windowDays ?? 14;
  const minCount = opts?.minCount ?? 2;
  const excludeWeak = opts?.excludeWeak ?? true;
  const list = getJSON<MistakeEntry[]>(MISTAKES_KEY, []);
  const cutoff = Date.now() - windowDays * 86_400_000;
  const map = new Map<string, MistakePattern>();
  for (const e of list) {
    if (e.ts < cutoff) continue;
    if (excludeWeak && e.quality === 'weak_generated') continue;
    let p = map.get(e.label);
    if (!p) {
      p = {
        label: e.label,
        count: 0,
        lastQuestionId: e.qid,
        lastTs: e.ts,
        qualities: { good: 0, ambiguous: 0, weak_generated: 0 },
      };
      map.set(e.label, p);
    }
    p.count++;
    if (e.ts > p.lastTs) { p.lastTs = e.ts; p.lastQuestionId = e.qid; }
    p.qualities[e.quality] = (p.qualities[e.quality] ?? 0) + 1;
  }
  return Array.from(map.values())
    .filter(p => p.count >= minCount)
    .sort((a, b) => b.count - a.count);
}

// ── Duel History ────────────────────────────────────────────────
export interface DuelResult {
  ts: number;
  won: boolean;
  tied: boolean;
  myScore: number;
  opScore: number;
  total: number;
  opponentName: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'mixed';
}

const DUEL_HISTORY_KEY = 'bagrut_duel_history';
const DUEL_HISTORY_KEEP = 50;

export function addDuelResult(r: DuelResult): void {
  const list = getJSON<DuelResult[]>(DUEL_HISTORY_KEY, []);
  list.push(r);
  if (list.length > DUEL_HISTORY_KEEP) list.splice(0, list.length - DUEL_HISTORY_KEEP);
  setJSON(DUEL_HISTORY_KEY, list);
}

export function getDuelHistory(): DuelResult[] {
  return getJSON<DuelResult[]>(DUEL_HISTORY_KEY, []);
}

export interface DuelStats {
  total: number;
  wins: number;
  losses: number;
  ties: number;
  winRate: number;        // 0–100, only counts decisive (non-tie) duels
  currentWinStreak: number;
  bestWinStreak: number;
  lastTs: number | null;
}

export function getDuelStats(): DuelStats {
  const list = getDuelHistory();
  if (list.length === 0) {
    return { total: 0, wins: 0, losses: 0, ties: 0, winRate: 0, currentWinStreak: 0, bestWinStreak: 0, lastTs: null };
  }
  const wins = list.filter(r => r.won).length;
  const ties = list.filter(r => r.tied).length;
  const losses = list.length - wins - ties;
  const decisive = wins + losses;
  // Streak: walk newest→oldest, ties pass-through, loss breaks the streak
  const sorted = [...list].sort((a, b) => b.ts - a.ts);
  let cur = 0;
  for (const r of sorted) {
    if (r.won) cur++;
    else if (!r.tied) break;
  }
  // Best streak: scan oldest→newest
  const chrono = [...list].sort((a, b) => a.ts - b.ts);
  let best = 0;
  let run = 0;
  for (const r of chrono) {
    if (r.won) { run++; if (run > best) best = run; }
    else if (!r.tied) run = 0;
  }
  return {
    total: list.length,
    wins,
    losses,
    ties,
    winRate: decisive > 0 ? Math.round((wins / decisive) * 100) : 0,
    currentWinStreak: cur,
    bestWinStreak: best,
    lastTs: sorted[0].ts,
  };
}

// ── Adaptive Skill Level (ELO-lite) ─────────────────────────────
/**
 * Float in [1, 5] tracking student's perceived difficulty level. Updated after
 * every answer via a simple logistic-ELO rule:
 *   expected   = σ((skill − q.difficulty) / SCALE)
 *   skill_new  = clamp(skill + K · (actual − expected), 1, 5)
 *
 * Used by adaptive practice mode to pick questions whose difficulty is closest
 * to the current skill (sweet spot for learning — not too easy, not too hard).
 */
const SKILL_KEY = 'bagrut_skill_level';
const SKILL_HISTORY_KEY = 'bagrut_skill_history';
const DEFAULT_SKILL = 3.0;
const SKILL_K = 0.3;     // learning rate
const SKILL_SCALE = 1.5; // logistic scale on the 1–5 axis

export function getSkillLevel(): number {
  const v = getJSON<number>(SKILL_KEY, DEFAULT_SKILL);
  return Number.isFinite(v) ? v : DEFAULT_SKILL;
}

export function updateSkillLevel(correct: boolean, questionDifficulty: number): number {
  const qDiff = Math.max(1, Math.min(5, questionDifficulty || 3));
  const cur = getSkillLevel();
  const expected = 1 / (1 + Math.exp(-(cur - qDiff) / SKILL_SCALE));
  const actual = correct ? 1 : 0;
  const next = Math.max(1, Math.min(5, cur + SKILL_K * (actual - expected)));
  setJSON(SKILL_KEY, Math.round(next * 100) / 100);
  return next;
}

/**
 * Record one daily skill snapshot so the summary can show "X.X → Y.Y" deltas
 * across sessions. Replaces today's entry if present. Keeps last 90 days.
 */
export interface SkillSnapshot { date: string; skill: number }
export function recordSkillSnapshot(): void {
  const today = new Date().toISOString().slice(0, 10);
  const list = getJSON<SkillSnapshot[]>(SKILL_HISTORY_KEY, []);
  const idx = list.findIndex(s => s.date === today);
  const entry: SkillSnapshot = { date: today, skill: getSkillLevel() };
  if (idx >= 0) list[idx] = entry; else list.push(entry);
  if (list.length > 90) list.splice(0, list.length - 90);
  setJSON(SKILL_HISTORY_KEY, list);
}

/** Returns null until at least one prior-day snapshot exists. */
export function getSkillTrend(): { current: number; weekAgo: number; delta: number } | null {
  const list = getJSON<SkillSnapshot[]>(SKILL_HISTORY_KEY, []);
  if (list.length === 0) return null;
  const sorted = [...list].sort((a, b) => a.date.localeCompare(b.date));
  const current = getSkillLevel();
  const weekAgoTarget = new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10);
  let weekAgo: SkillSnapshot | undefined;
  for (const s of sorted) {
    if (s.date <= weekAgoTarget) weekAgo = s;
    else break;
  }
  if (!weekAgo) return null;
  return { current, weekAgo: weekAgo.skill, delta: Math.round((current - weekAgo.skill) * 100) / 100 };
}

// ── Level-up detection (for celebration modal) ─────────────────
const LAST_SEEN_LEVEL_KEY = 'bagrut_last_seen_level';

export function getLastSeenLevel(): number {
  return getJSON<number>(LAST_SEEN_LEVEL_KEY, 1);
}

export function setLastSeenLevel(level: number): void {
  setJSON(LAST_SEEN_LEVEL_KEY, level);
}

// ── Onboarding ──────────────────────────────────────────────────
const ONBOARDED_KEY = 'bagrut_onboarded';

export function isOnboarded(): boolean {
  return getJSON<boolean>(ONBOARDED_KEY, false);
}

export function setOnboarded(): void {
  setJSON(ONBOARDED_KEY, true);
}

/** Clear the onboarded flag so the intro flow runs again on next app load.
 *  Used by the "Show intro again" action in Settings. */
export function resetOnboarded(): void {
  removeItem(ONBOARDED_KEY);
}

// ── Full Tutorial (separate from the 4-slide onboarding) ────────
const TUTORIAL_SEEN_KEY = 'bagrut_tutorial_seen';

export function hasSeenTutorial(): boolean {
  return getJSON<boolean>(TUTORIAL_SEEN_KEY, false);
}

export function markTutorialSeen(): void {
  setJSON(TUTORIAL_SEEN_KEY, true);
}

export function resetTutorialSeen(): void {
  removeItem(TUTORIAL_SEEN_KEY);
}

// ── Streak Freeze + status ──────────────────────────────────────
/**
 * Streak loops — computed locally from the bagrut_weekly daily history so
 * the user keeps a working streak even offline / when the server is asleep.
 * "Freeze tokens" are an XP-purchaseable power-up that auto-saves a missed
 * day (consumed silently if a gap of exactly one day appears).
 */
export type StreakStatus = 'active' | 'in_danger' | 'broken' | 'none';

const FREEZE_TOKENS_KEY = 'bagrut_freeze_tokens';
const FREEZE_LAST_USED_KEY = 'bagrut_freeze_last_used';

export function getFreezeTokens(): number {
  return getJSON<number>(FREEZE_TOKENS_KEY, 0);
}

export function setFreezeTokens(n: number): void {
  setJSON(FREEZE_TOKENS_KEY, Math.max(0, Math.floor(n)));
}

/** Decrement and return true if a token was available, false otherwise. */
export function useFreezeToken(): boolean {
  const cur = getFreezeTokens();
  if (cur <= 0) return false;
  setFreezeTokens(cur - 1);
  setJSON(FREEZE_LAST_USED_KEY, new Date().toISOString().slice(0, 10));
  return true;
}

/**
 * Compute streak status from the local weekly history. Returns:
 *   - active     — student practiced today
 *   - in_danger  — streak active but no activity in the last >20h (will break at midnight)
 *   - broken     — streak ≥1 was active recently but a day got skipped without freeze
 *   - none       — no streak in progress
 *
 * Also returns the current streak length (consecutive days with ≥1 attempt up to today).
 */
export function getStreakStatus(): { status: StreakStatus; days: number; lastActivityDate: string | null } {
  const data = getJSON<{ days: { date: string; count: number }[] }>(STORAGE_KEYS.WEEKLY, { days: [] });
  const days = (data.days || []).filter(d => d.count > 0).sort((a, b) => a.date.localeCompare(b.date));
  if (days.length === 0) {
    return { status: 'none', days: 0, lastActivityDate: null };
  }

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const lastDay = days[days.length - 1];

  // Walk backwards from today/yesterday counting consecutive days
  let streak = 0;
  let cursor = lastDay.date === today ? today : lastDay.date === yesterday ? yesterday : null;
  if (cursor === null) {
    return { status: 'broken', days: 0, lastActivityDate: lastDay.date };
  }
  const dateSet = new Set(days.map(d => d.date));
  // Convert cursor → Date object for arithmetic
  const stepBack = (d: string): string => {
    const dt = new Date(d + 'T00:00:00');
    dt.setDate(dt.getDate() - 1);
    return dt.toISOString().slice(0, 10);
  };
  while (dateSet.has(cursor)) {
    streak += 1;
    cursor = stepBack(cursor);
  }

  let status: StreakStatus;
  if (lastDay.date === today) {
    status = 'active';
  } else if (lastDay.date === yesterday) {
    // Streak still alive but no activity yet today — danger if late in day
    const hour = new Date().getHours();
    status = hour >= 18 ? 'in_danger' : 'active';
  } else {
    status = streak > 0 ? 'broken' : 'none';
  }

  return { status, days: streak, lastActivityDate: lastDay.date };
}

// ── Avatar (player identity) ────────────────────────────────────
const AVATAR_KEY = 'bagrut_avatar_id';

export function getAvatarId(): string | null {
  return getJSON<string | null>(AVATAR_KEY, null);
}

export function setAvatarId(id: string): void {
  setJSON(AVATAR_KEY, id);
}

// ── Lesson views ───────────────────────────────────────────────
// Tracks which curriculum topics the student has actually opened in the
// Lesson screen. Powers the Learn lane: "continue where you left off",
// "first lesson" for new users, and the ✓ chip on topic cards. Local only
// — the server doesn't need to know which lessons were read.
const LESSON_VIEWS_KEY = 'bagrut_lesson_views';

interface LessonViewMap { [topicId: string]: number /* timestamp ms */ }

export function markLessonViewed(topicId: string): void {
  if (!topicId) return;
  const data = getJSON<LessonViewMap>(LESSON_VIEWS_KEY, {});
  data[topicId] = Date.now();
  setJSON(LESSON_VIEWS_KEY, data);
}

export function getViewedLessons(): LessonViewMap {
  return getJSON<LessonViewMap>(LESSON_VIEWS_KEY, {});
}

export function getLastViewedLesson(): { topicId: string; ts: number } | null {
  const map = getViewedLessons();
  const entries = Object.entries(map);
  if (entries.length === 0) return null;
  entries.sort(([, a], [, b]) => b - a);
  return { topicId: entries[0][0], ts: entries[0][1] };
}

// ── Clear All Bagrut Data ───────────────────────────────────────
export function clearBagrutData(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('bagrut_'));
    keys.forEach(k => localStorage.removeItem(k));
  } catch (e) {
    console.warn('[Storage] Failed to clear data:', e);
  }
}
